/**
 * Error Boundary Component
 * Catches React errors and displays a fallback UI
 * Improves UX by gracefully handling component errors
 */

import React from 'react';
import styled from 'styled-components';
import { FiAlertCircle, FiRefreshCw, FiHome } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const ErrorContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: var(--bg-primary);
  color: var(--text-primary);
`;

const ErrorContent = styled.div`
  max-width: 600px;
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 4rem;
  color: var(--accent-primary);
  margin-bottom: 1.5rem;
`;

const ErrorTitle = styled.h1`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: var(--text-primary);
`;

const ErrorMessage = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const ErrorDetails = styled.details`
  margin: 2rem 0;
  text-align: left;
  background: var(--bg-secondary);
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid var(--border-color);
`;

const ErrorSummary = styled.summary`
  cursor: pointer;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  
  &:hover {
    color: var(--accent-primary);
  }
`;

const ErrorStack = styled.pre`
  font-size: 0.875rem;
  color: var(--text-secondary);
  white-space: pre-wrap;
  word-wrap: break-word;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: var(--bg-primary);
  border-radius: 4px;
  overflow-x: auto;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${props => props.primary ? 'var(--accent-gradient)' : 'var(--bg-secondary)'};
  color: ${props => props.primary ? 'white' : 'var(--text-primary)'};
  border: ${props => props.primary ? 'none' : '2px solid var(--border-color)'};
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(_error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console in development, in production this would go to error tracking service
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Store error details for display
    this.setState({
      error,
      errorInfo
    });

    // TODO: Send error to logging service (e.g., Sentry) in production
    // if (process.env.NODE_ENV === 'production') {
    //   // Send to error tracking service
    // }
  }

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null,
      errorInfo: null 
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      return (
        <ErrorContainer>
          <ErrorContent>
            <ErrorIcon>
              <FiAlertCircle />
            </ErrorIcon>
            <ErrorTitle>Something went wrong</ErrorTitle>
            <ErrorMessage>
              We're sorry, but something unexpected happened. Please try refreshing the page or return to the home page.
            </ErrorMessage>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <ErrorDetails>
                <ErrorSummary>Error Details (Development Only)</ErrorSummary>
                <ErrorStack>
                  {this.state.error.toString()}
                  {this.state.errorInfo && (
                      <>
                        {'\n\n'}
                        Component Stack:
                        {'\n'}
                        {this.state.errorInfo.componentStack}
                      </>
                    )}
                </ErrorStack>
              </ErrorDetails>
            )}

            <ButtonGroup>
              <Button primary onClick={this.handleReset}>
                <FiRefreshCw />
                Try Again
              </Button>
              <StyledLink to="/">
                <FiHome />
                Go Home
              </StyledLink>
            </ButtonGroup>
          </ErrorContent>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

